// @ts-nocheck
import { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import { useRouter } from 'expo-router';
import { ArrowRight, ArrowLeft } from 'lucide-react-native';
import { theme } from '@/constants/theme';
import { signInWithGoogle, supabase } from '@/utils/supabase';
import { BetaBanner } from '@/components/BetaBanner';
import { AccessibilityButton } from '@/components/AccessibilityButton';

export default function MentorRegisterScreen() {
  const router = useRouter();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [isGoogleAuthenticated, setIsGoogleAuthenticated] = useState(false);

  const handleGoogleRegister = async () => {
    setLoading(true);
    setError('');
    setStatusMessage('מתחבר עם Google...');

    try {
      const result = await signInWithGoogle();

      if (!result.success) {
        setError('לא הצלחנו להתחבר עם Google. נסה שוב.');
        setStatusMessage('');
        setLoading(false);
        return;
      }

      setStatusMessage('מעביר...');
    } catch (err) {
      console.error('Google registration error:', err);
      setError('שגיאה בהתחברות עם Google');
      setStatusMessage('');
      setLoading(false);
    }
  };

  const validateForm = () => {
    if (!fullName.trim() || !email.trim() || !phone.trim()) {
      setError('נא למלא את כל השדות החובה');
      return false;
    }

    if (!isGoogleAuthenticated) {
      setError('יש להתחבר עם Google קודם');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('כתובת אימייל לא תקינה');
      return false;
    }

    const phoneRegex = /^05\d{8}$/;
    if (!phoneRegex.test(phone.replace(/[-\s]/g, ''))) {
      setError('מספר טלפון לא תקין (צריך להתחיל ב-05)');
      return false;
    }

    return true;
  };

  const handleRegister = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data: existingMentor } = await supabase
        .from('mentors')
        .select('email')
        .eq('email', email.trim())
        .maybeSingle();

      if (existingMentor) {
        setError('משתמש עם אימייל זה כבר קיים במערכת');
        return;
      }

      const { error: mentorError, data: newMentor } = await supabase
        .from('mentors')
        .insert([
          {
            email: email.trim(),
            full_name: fullName.trim(),
            phone: phone.trim(),
            bio: null,
            email_notifications: true,
          },
        ])
        .select()
        .single();

      if (mentorError) {
        setError('שגיאה ביצירת פרופיל המנטור');
        return;
      }

      await supabase
        .from('leads')
        .update({ mentor_id: newMentor.id })
        .eq('mentor_email', email.trim())
        .is('mentor_id', null);

      router.replace('/mentor/dashboard');
    } catch (error) {
      console.error('Registration error:', error);
      setError('שגיאה בהרשמה, נסה שוב');
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <View style={styles.topBar}>
        <Pressable
          onPress={() => router.back()}
          style={styles.backButton}
          disabled={loading}
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel="חזרה לדף הבית"
          {...(Platform.OS === 'web' && {
            'aria-label': 'חזרה לדף הבית',
          } as any)}
        >
          <ArrowLeft color={theme.colors.text} size={24} aria-hidden={true} />
          <Text style={styles.backText}>חזרה</Text>
        </Pressable>
        <BetaBanner compact />
      </View>

      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.content}>
          <Text
            style={styles.title}
            accessible={true}
            accessibilityRole="header"
            {...(Platform.OS === 'web' && {
              role: 'heading',
              'aria-level': 1,
            } as any)}
          >
            הרשמה למנטורים
          </Text>

          <Pressable
            style={({ pressed }) => [
              styles.googleButton,
              pressed && styles.googleButtonPressed,
              loading && styles.googleButtonDisabled,
            ]}
            onPress={handleGoogleRegister}
            disabled={loading}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel="הרשם עם Google"
            accessibilityHint="לחץ להרשמה עם חשבון Google"
            accessibilityState={{ disabled: loading }}
            {...(Platform.OS === 'web' && {
              'aria-label': 'הרשם עם Google',
            } as any)}
          >
            {loading ? (
              <ActivityIndicator color="#000" />
            ) : (
              <Text style={styles.googleButtonText}>הרשם עם Google</Text>
            )}
          </Pressable>

          {statusMessage ? (
            <Text
              style={styles.statusText}
              accessibilityLiveRegion="polite"
            >
              {statusMessage}
            </Text>
          ) : null}

          <View style={styles.form}>
            {error ? (
              <Text
                style={styles.errorText}
                accessibilityRole="alert"
                accessibilityLiveRegion="assertive"
              >
                {error}
              </Text>
            ) : null}

            <View style={styles.inputGroup}>
              <Text style={styles.label}>שם מלא</Text>
              <TextInput
                style={styles.input}
                value={fullName}
                onChangeText={(text) => {
                  setFullName(text);
                  setError('');
                }}
                placeholder="ישראל ישראלי"
                placeholderTextColor={theme.colors.textLight}
                autoCapitalize="words"
                editable={!loading}
                accessible={true}
                accessibilityLabel="שם מלא"
                accessibilityHint="הזן את שמך המלא"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>אימייל</Text>
              <TextInput
                style={[styles.input, isGoogleAuthenticated && styles.inputDisabled]}
                value={email}
                onChangeText={(text) => {
                  setEmail(text);
                  setError('');
                }}
                placeholder="mentor@example.com"
                placeholderTextColor={theme.colors.textLight}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                editable={!isGoogleAuthenticated && !loading}
                accessible={true}
                accessibilityLabel="כתובת אימייל"
                accessibilityHint={isGoogleAuthenticated ? "האימייל התקבל מ-Google" : "הזן את כתובת האימייל שלך"}
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>טלפון</Text>
              <TextInput
                style={styles.input}
                value={phone}
                onChangeText={(text) => {
                  setPhone(text);
                  setError('');
                }}
                placeholder="0501234567"
                placeholderTextColor={theme.colors.textLight}
                keyboardType="phone-pad"
                editable={!loading}
                accessible={true}
                accessibilityLabel="מספר טלפון"
                accessibilityHint="הזן מספר טלפון נייד"
              />
            </View>

            <Pressable
              style={({ pressed }) => [
                styles.registerButton,
                pressed && styles.registerButtonPressed,
                loading && styles.registerButtonDisabled,
              ]}
              onPress={handleRegister}
              disabled={loading}
              accessible={true}
              accessibilityRole="button"
              accessibilityLabel="הרשם עכשיו"
              accessibilityHint="לחץ לסיום תהליך ההרשמה"
              accessibilityState={{ disabled: loading }}
              {...(Platform.OS === 'web' && {
                'aria-label': 'הרשם עכשיו',
              } as any)}
            >
              {loading ? (
                <ActivityIndicator color="#FAF5EA" />
              ) : (
                <>
                  <Text style={styles.registerButtonText}>הרשם עכשיו</Text>
                  <ArrowRight color="#FAF5EA" size={20} />
                </>
              )}
            </Pressable>
          </View>

          <View style={styles.linksContainer}>
            <Text style={styles.normalText}>כבר יש לך חשבון?</Text>
            <Pressable
              onPress={() => router.push('/mentor/login')}
              disabled={loading}
              accessible={true}
              accessibilityRole="link"
              accessibilityLabel="התחבר לחשבון קיים"
              {...(Platform.OS === 'web' && {
                'aria-label': 'התחבר לחשבון קיים',
              } as any)}
            >
              <Text style={styles.linkText}>התחבר</Text>
            </Pressable>
          </View>
        </View>
      </ScrollView>

      <AccessibilityButton />
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  topBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingTop: Platform.OS === 'ios' ? 60 : theme.spacing.lg,
    paddingBottom: theme.spacing.sm,
    backgroundColor: theme.colors.background,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
    padding: theme.spacing.sm,
  },
  backText: {
    fontSize: 16,
    fontFamily: theme.fonts.medium,
    color: theme.colors.text,
  },
  scrollContent: {
    flexGrow: 1,
  },
  content: {
    paddingHorizontal: theme.spacing.lg,
    paddingTop: theme.spacing.xl,
    paddingBottom: theme.spacing.xl,
    maxWidth: 400,
    alignSelf: 'center',
    width: '100%',
  },
  title: {
    fontSize: 32,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
    marginBottom: theme.spacing.xl,
    textAlign: 'center',
  },
  form: {
    gap: theme.spacing.md,
  },
  inputGroup: {
    gap: theme.spacing.xs,
  },
  label: {
    fontSize: 14,
    fontFamily: theme.fonts.medium,
    color: theme.colors.text,
    textAlign: 'right',
  },
  input: {
    backgroundColor: theme.colors.secondary,
    borderRadius: theme.borderRadius.lg,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.md,
    fontSize: 16,
    fontFamily: theme.fonts.regular,
    color: theme.colors.text,
    textAlign: 'right',
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  inputDisabled: {
    opacity: 0.6,
    backgroundColor: theme.colors.background,
  },
  errorText: {
    fontSize: 14,
    fontFamily: theme.fonts.medium,
    color: '#FF6B6B',
    textAlign: 'center',
    backgroundColor: 'rgba(255, 107, 107, 0.1)',
    padding: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
  },
  registerButton: {
    backgroundColor: theme.colors.primary,
    borderRadius: theme.borderRadius.lg,
    paddingVertical: theme.spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.xs,
    marginTop: theme.spacing.md,
  },
  registerButtonPressed: {
    backgroundColor: theme.colors.primaryDark,
    transform: [{ scale: 0.98 }],
  },
  registerButtonDisabled: {
    opacity: 0.6,
  },
  registerButtonText: {
    fontSize: 18,
    fontFamily: theme.fonts.bold,
    color: '#FAF5EA',
  },
  linksContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.xs,
    marginTop: theme.spacing.xl,
  },
  linkText: {
    fontSize: 14,
    fontFamily: theme.fonts.medium,
    color: theme.colors.primary,
  },
  normalText: {
    fontSize: 14,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
  },
  googleButton: {
    backgroundColor: '#FAF5EA',
    borderRadius: theme.borderRadius.lg,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.xl,
    borderWidth: 1,
    borderColor: theme.colors.border,
    marginBottom: theme.spacing.lg,
  },
  googleButtonPressed: {
    backgroundColor: '#F5F5F5',
    transform: [{ scale: 0.98 }],
  },
  googleButtonDisabled: {
    opacity: 0.6,
  },
  googleButtonText: {
    fontSize: 16,
    fontFamily: theme.fonts.bold,
    color: '#1F1F1F',
    textAlign: 'center',
  },
  statusText: {
    color: theme.colors.primary,
    marginBottom: theme.spacing.md,
    textAlign: 'center',
    fontFamily: theme.fonts.medium,
  },
});
